import { atom } from "jotai";
import { atomEffect } from "jotai-effect";

import { generateShortUuid } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { $createDocumentWorkflow, type InitialPage } from "../document";
import { $documentsList, docsListItemType } from "../document-list";
import { env } from "../env";

const logger = new Logger("daily-notes");

export const DAILY_NOTES_DOCUMENT_TYPE = "notes" as const;
export const DAILY_NOTES_TITLE = "Daily Notes";

let isCreatingDailyNotes = false;

/**
 * Returns all daily notes documents found in the document list.
 * There may be multiple if a user has a local daily notes document
 * and also has a remote one with a different ID.
 */
export const $allDailyNotesDocuments = atom((get) => {
	const docsList = get($documentsList);
	if (docsList.status === "initializing") {
		return [];
	}

	const docs = Object.values(docsList.documents);
	return docs.filter((doc) => docsListItemType(doc) === DAILY_NOTES_DOCUMENT_TYPE);
});
$allDailyNotesDocuments.debugLabel = "allDailyNotesDocuments";

/**
 * Returns a single daily notes document, preferring local-only documents
 * over remote ones. If multiple daily notes documents exist, use
 * $allDailyNotesDocuments to let the user choose.
 */
export const $dailyNotesDocument = atom((get) => {
	const allDocs = get($allDailyNotesDocuments);
	if (allDocs.length === 0) {
		return undefined;
	}

	// Prefer local-only documents (those without remote entries)
	const localOnly = allDocs.find(
		(doc) => Object.keys(doc.local).length > 0 && Object.keys(doc.remote).length === 0
	);
	if (localOnly) {
		return localOnly;
	}

	// Fall back to the first one found
	return allDocs[0];
});
$dailyNotesDocument.debugLabel = "dailyNotesDocument";

export const $hasDailyNotesDocument = atom((get) => {
	const dailyNotes = get($dailyNotesDocument);
	return dailyNotes !== undefined;
});
$hasDailyNotesDocument.debugLabel = "hasDailyNotesDocument";

const isApp = env.isDesktopApp || env.isMobileApp;

export const $createDailyNotesDocument = atom(null, async (_get, set) => {
	if (!isApp) {
		return undefined;
	}

	if (isCreatingDailyNotes) {
		logger.debug("Already creating daily notes document, skipping");
		return undefined;
	}

	isCreatingDailyNotes = true;

	try {
		const documentId = generateShortUuid();
		logger.debug("Creating daily notes document", { documentId });

		// Generate today's date info for the initial page
		const now = new Date();
		const year = now.getFullYear().toString();
		const month = (now.getMonth() + 1).toString().padStart(2, "0");
		const day = now.getDate().toString().padStart(2, "0");
		const dateSlug = `${year}-${month}-${day}`;
		const dayTitle = now.toLocaleDateString(undefined, {
			weekday: "short",
			year: "numeric",
			month: "long",
			day: "numeric",
		});

		// Create a single flat page for today's date
		// Pages are stored as flat files: daily/2026-01-21.md
		const initialPages: InitialPage[] = [
			{
				id: dateSlug,
				title: dayTitle,
				slug: dateSlug,
				parentPageID: null,
				path: `daily/${dateSlug}.md`,
			},
		];

		const docsListItem = await set($createDocumentWorkflow, {
			documentId,
			type: DAILY_NOTES_DOCUMENT_TYPE,
			title: DAILY_NOTES_TITLE,
			initialPages,
		});

		logger.info("Daily notes document created with today page", { documentId, dateSlug });

		return docsListItem;
	} finally {
		isCreatingDailyNotes = false;
	}
});
$createDailyNotesDocument.debugLabel = "createDailyNotesDocument";

export const $ensureDailyNotesDocumentEffect = atomEffect((get, set) => {
	if (!isApp) {
		return;
	}

	const docsList = get($documentsList);

	if (docsList.status !== "idle") {
		return;
	}

	const dailyNotesDoc = get($dailyNotesDocument);
	if (dailyNotesDoc) {
		logger.debug("Daily notes document already exists", { id: dailyNotesDoc.id });
		return;
	}

	if (isCreatingDailyNotes) {
		logger.debug("Already creating daily notes document, skipping effect");
		return;
	}

	logger.info("No daily notes document found, creating one");
	void set($createDailyNotesDocument);
});
$ensureDailyNotesDocumentEffect.debugLabel = "ensureDailyNotesDocumentEffect";
